import { getPaginatedSalesCampaigns } from '../../../api/salesCampaignApi'
import YesOrNoTag from '../../../components/common/YesOrNoTag'
import LinkButton from '../../../components/link-button/link-button'
import SalesCampaignFilter from '../../../components/sales-campaigns/sales-campaign-filter/sales-campaign-filter'
import { getDateTime, getSearchParams, getSearchValues } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import { ReduxStore } from '../../../store'
import styles from '../../sales-campaigns-container/sales-campaigns/sales-campaigns.module.less'
import {
  ISalesCampaignFilterParams,
  PaginatedSalesCampaign,
  PaginatedSalesCampaignFilterParams,
  ISalesCampaign,
  SalesCampaignStatusEnums,
} from '../share-type'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Col, Image, Row, Space, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { compose } from 'redux'

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const SalesCampaigns = () => {
  const navigate = useNavigate()
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [searchValues, setSearchValues] = useState<ISalesCampaignFilterParams>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [salesCampaigns, setSalesCampaigns] = useState<ISalesCampaign[]>()

  useEffect(() => {
    const newSearchValues = getSearchValues(
      [
        { key: 'name', isBoolean: false },
        { key: 'status', isBoolean: false },
        { key: 'size', isBoolean: false, default: defaultPage.pageSize },
        { key: 'page', isBoolean: false, default: defaultPage.currentPage - 1 },
      ],
      searchParams,
    )

    const data = {
      ...newSearchValues,
      size: Number(newSearchValues.size),
      page: Number(newSearchValues.page),
    }
    setSearchValues(newSearchValues)
    getPaginatedSalesCampaignsAction(data)
  }, [])

  const getPaginatedSalesCampaignsAction = (data: PaginatedSalesCampaignFilterParams) => {
    getPaginatedSalesCampaigns(data).then((res: PaginatedSalesCampaign) => {
      setSalesCampaigns(res.data)
      setPageInfo({
        ...res.pageInfo,
        currentPage: res.pageInfo.currentPage + 1,
      })
    })
  }

  const onSearch = (values: ISalesCampaignFilterParams) => {
    const data = {
      page: 0,
      size: pageInfo.pageSize,
      ...values,
    }
    setSearchValues(values)
    setSearchParams(getSearchParams(values))
    getPaginatedSalesCampaignsAction(data)
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    const data = {
      ...searchValues,
      size: pageSize,
      page: page - 1,
    }

    getPaginatedSalesCampaignsAction(data)
  }

  const columns: ColumnsType<ISalesCampaign> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
    },
    {
      title: translate('name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: translate('salesCampaign.plan'),
      dataIndex: 'planName',
      key: 'planName',
    },
    {
      title: translate('salesCampaign.couponTemplate'),
      dataIndex: 'couponTemplateName',
      key: 'couponTemplateName',
    },
    {
      title: translate('salesCampaign.floatingPortal'),
      dataIndex: 'floatingPortalUrl',
      key: 'floatingPortalUrl',
      render: (floatingPortalUrl?: string) => {
        return floatingPortalUrl ? (
          <Image src={floatingPortalUrl} style={{ maxWidth: '50px', maxHeight: '50px' }} />
        ) : (
          ''
        )
      },
    },
    {
      title: translate('salesCampaign.chargeBanner'),
      dataIndex: 'chargeBannerUrl',
      key: 'chargeBannerUrl',
      render: (chargeBannerUrl?: string) => {
        return chargeBannerUrl ? (
          <Image src={chargeBannerUrl} style={{ maxWidth: '50px', maxHeight: '50px' }} />
        ) : (
          ''
        )
      },
    },
    {
      title: translate('salesCampaign.enableCouponReminder'),
      dataIndex: 'enableCouponReminder',
      key: 'enableCouponReminder',
      render: (enableCouponReminder: boolean) => {
        return <YesOrNoTag value={enableCouponReminder} />
      },
    },
    {
      title: translate('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: SalesCampaignStatusEnums) => {
        return (
          <Tag color={status === SalesCampaignStatusEnums.INACTIVE ? 'red' : 'green'}>{status}</Tag>
        )
      },
    },
    {
      title: translate('salesCampaign.startDatetime'),
      dataIndex: 'startDatetime',
      key: 'startDatetime',
      render: (startDatetime: string) => {
        return <span>{getDateTime(startDatetime)}</span>
      },
    },
    {
      title: translate('salesCampaign.endDatetime'),
      dataIndex: 'endDatetime',
      key: 'endDatetime',
      render: (endDatetime: string) => {
        return <span>{getDateTime(endDatetime)}</span>
      },
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      render: (record: ISalesCampaign) => (
        <Space>
          <AuthPermission permission={PermissionsEnum.SALES_CAMPAIGN_READ}>
            <LinkButton to={`/sales-campaigns/${record.id}`}>{translate('view')}</LinkButton>
          </AuthPermission>
          <AuthPermission permission={PermissionsEnum.SALES_CAMPAIGN_WRITE}>
            <LinkButton to={`/sales-campaigns/${record.id}/edit`}>{translate('edit')}</LinkButton>
          </AuthPermission>
        </Space>
      ),
    },
  ]

  return (
    <Space className={styles['sales-campaign-list-space']} orientation="vertical" size={16}>
      <Row justify="center" align={'middle'}>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['left-col']}>
          <div>{translate('navBar.salesCampaignsTitle')}</div>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['right-col']}>
          <AuthPermission permission={PermissionsEnum.SALES_CAMPAIGN_WRITE}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/sales-campaigns/new')}
            >
              {translate('navBar.newSalesCampaign')}
            </Button>
          </AuthPermission>
        </Col>
      </Row>

      <div className={styles['filter-bar']}>
        <SalesCampaignFilter onSearch={onSearch} formData={searchValues} />
      </div>

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={salesCampaigns}
        pagination={{
          current: pageInfo.currentPage,
          pageSize: pageInfo.pageSize,
          total: pageInfo.count,
          onChange: paginationOnChange,
        }}
      />
    </Space>
  )
}

const mapStateToProps = (state: ReduxStore) => {
  return {
    locale: state.global.locale,
  }
}

const withConnect = connect(mapStateToProps)

export default compose(withConnect)(SalesCampaigns)
